import type {
  ActiveKeyspacePointer,
  Family,
  KeyspaceDirectoryEntry,
  ReleaseDefinition,
  SyncPhase,
} from "@legal-500/redis-db-manager-types";
import { authTokenManager } from "@legal-500/auth-client";
import * as api from "./client.js";
import type { BffRedisBreakdownResponse } from "./bffRedisBreakdown.types.js";

export type { ReleaseDefinition };
export type { BffRedisBreakdownResponse } from "./bffRedisBreakdown.types.js";

export type SearchIndexState = {
  numDocs: number;
  percentIndexed: number;
  hashIndexingFailures: number;
  ready: boolean;
  reasons: string[];
};

export type DbManagerKeyspace = KeyspaceDirectoryEntry & {
  searchIndex?: SearchIndexState | null;
};

export const SEARCH_INDEX_NOT_READY = "SEARCH_INDEX_NOT_READY";

export type SearchIndexNotReadyDetails = {
  numDocs: number | null;
  percentIndexed: number | null;
  hashIndexingFailures: number | null;
  reasons: string[];
};

export type ListKeyspacesResponse = {
  keyspaces: DbManagerKeyspace[];
  activeKeyspaces: Record<Family, ActiveKeyspacePointer | null>;
  requestId: string;
};

export type GetKeyspaceResponse = {
  keyspace: DbManagerKeyspace;
  requestId: string;
};

export type CreateKeyspaceResponse = {
  keyspace: KeyspaceDirectoryEntry;
  requestId: string;
};

export type PageTypesResponse = {
  pageTypes: string[];
  requestId: string;
};

export type TriggerSyncBody = {
  keyspace: string;
  family?: string;
  clearAllData: boolean;
  pageTypes: string[] | null;
  snapshotS3Path?: string;
  seedFromKeyspace?: string | null;
  releaseDefinition?: ReleaseDefinition;
  force?: boolean;
};

export const OTHER_SLOT_SYNC_RUNNING = "OTHER_SLOT_SYNC_RUNNING";

export type OtherSlotSyncRunningDetails = {
  runningKeyspace: string;
  runId: string;
  startedAt: number;
  triggeredBy: string;
};

export type TriggerSyncResponse = {
  runId: string;
  requestId: string;
};

export type SetActiveKeyspaceBody = {
  keyspace: string;
  family?: string;
};

export type SetActiveKeyspaceResponse = {
  activeKeyspace: ActiveKeyspacePointer;
  warning?: string;
  requestId: string;
};

export type CreatePreviewLinkBody = {
  label: string;
  ttlSeconds: number;
  family?: string;
};

export type CreatePreviewLinkResponse = {
  url: string;
  expiresAt: string;
  requestId: string;
};

export type KeyspaceProgressDetails = {
  runId: string;
  status: "running" | "completed" | "failed";
  startedAt: string;
  completedAt: string | null;
  totalPages: number;
  processedCount: number;
  failedCount: number;
  workersActive: number;
  pagesPerSecond: number;
  durationSeconds: number | null;
};

export type KeyspaceProgressResponse = {
  keyspace: string;
  runId: string | null;
  details: KeyspaceProgressDetails | null;
  /**
   * Pre-sync phase resolved from the k8s pod state by platform-api when the
   * sync subprocess hasn't yet started writing its own phase. Lets the UI
   * show "Waiting for a node" / "Pulling image" / "Downloading snapshot to
   * pod" instead of a frozen "Starting…" caption.
   */
  pendingPhase: SyncPhase | null;
  requestId: string;
};

export type AuditEntry = {
  id: string;
  occurredAt: string;
  fromKeyspace: string;
  toKeyspace: string;
  triggeredBy: string;
  triggeredByIp: string | null;
};

export type AuditListResponse = {
  items: AuditEntry[];
  nextCursor: string | null;
  requestId: string;
};

export type SyncLogSubscription = {
  close: () => void;
};

export type SyncLogHandlers = {
  onLine: (line: string) => void;
  onEnd: (state: string) => void;
  onError?: (err: Error) => void;
};

const DB_MANAGER_BASE = "/v1/platform/db-manager";

function encodeKeyForUrl(key: string): string {
  const utf8 = new TextEncoder().encode(key);
  let binary = "";
  for (let i = 0; i < utf8.length; i++) binary += String.fromCharCode(utf8[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export const dbManagerService = {
  listKeyspaces(): Promise<ListKeyspacesResponse> {
    return api.get<ListKeyspacesResponse>(`${DB_MANAGER_BASE}/keyspaces`);
  },

  getKeyspace(keyspace: string, family = "core"): Promise<GetKeyspaceResponse> {
    return api.get<GetKeyspaceResponse>(
      `${DB_MANAGER_BASE}/keyspaces/${keyspace}?family=${encodeURIComponent(family)}`,
    );
  },

  createKeyspace(keyspace: string, family = "core"): Promise<CreateKeyspaceResponse> {
    return api.post<CreateKeyspaceResponse>(`${DB_MANAGER_BASE}/keyspaces`, {
      keyspace,
      family,
    });
  },

  async listPageTypes(): Promise<string[]> {
    const response = await api.get<PageTypesResponse>(`${DB_MANAGER_BASE}/page-types`);
    return response.pageTypes;
  },

  triggerSync(body: TriggerSyncBody): Promise<TriggerSyncResponse> {
    return api.post<TriggerSyncResponse>(`${DB_MANAGER_BASE}/keyspaces/sync`, body);
  },

  async discardKeyspace(keyspace: string, family = "core"): Promise<void> {
    await api.post<{ keyspace: string; status: string; requestId: string }>(
      `${DB_MANAGER_BASE}/keyspaces/${keyspace}/discard?family=${encodeURIComponent(family)}`,
    );
  },

  async removeKeyspace(keyspace: string, family = "core"): Promise<void> {
    await api.del<{ keyspace: string; removed: boolean; requestId: string }>(
      `${DB_MANAGER_BASE}/keyspaces/${keyspace}?family=${encodeURIComponent(family)}`,
    );
  },

  listKeyspaceKeys(
    keyspace: string,
    opts: { match?: string; cursor?: string; count?: number } = {},
    signal?: AbortSignal,
  ): Promise<{
    keyspace: string;
    match: string;
    cursor: string;
    nextCursor: string;
    done: boolean;
    keys: Array<{ key: string; type: string }>;
    requestId: string;
  }> {
    const params = new URLSearchParams();
    if (opts.match) params.set("match", opts.match);
    if (opts.cursor) params.set("cursor", opts.cursor);
    if (opts.count !== undefined) params.set("count", String(opts.count));
    const qs = params.toString();
    const endpoint = `${DB_MANAGER_BASE}/keyspaces/${keyspace}/keys${qs ? `?${qs}` : ""}`;
    return signal ? api.getWithSignal(endpoint, signal) : api.get(endpoint);
  },

  getKeyspaceKey(
    keyspace: string,
    key: string,
  ): Promise<{
    keyspace: string;
    key: string;
    type: string;
    value: unknown;
    truncated: boolean;
    ttlSeconds: number;
    requestId: string;
  }> {
    const keyId = encodeKeyForUrl(key);
    return api.get(`${DB_MANAGER_BASE}/keyspaces/${keyspace}/keys/${keyId}`);
  },

  async deleteKeyspaceKey(keyspace: string, key: string): Promise<void> {
    const keyId = encodeKeyForUrl(key);
    await api.del<{ keyspace: string; key: string; deleted: boolean; requestId: string }>(
      `${DB_MANAGER_BASE}/keyspaces/${keyspace}/keys/${keyId}`,
    );
  },

  getKeyspaceMemory(): Promise<{
    keyspaces: Array<{ family: string; keyspace: string; keyCount: number; datasetBytes: number }>;
    totalDbSize: number;
    totalDatasetBytes: number;
    requestId: string;
  }> {
    return api.get(`${DB_MANAGER_BASE}/keyspace-memory`);
  },

  /**
   * Fetches the BFF Redis breakdown. The underlying SCAN can take ~45s on a
   * production cluster with 2M keys, so this call is wired with a generous
   * 90s timeout via AbortController.
   */
  async getBffRedisBreakdown(signal?: AbortSignal): Promise<BffRedisBreakdownResponse> {
    const TIMEOUT_MS = 90_000;
    const timeoutController = new AbortController();
    const onAbort = () => timeoutController.abort();
    if (signal) {
      if (signal.aborted) timeoutController.abort();
      else signal.addEventListener("abort", onAbort);
    }
    const timer = setTimeout(() => timeoutController.abort(), TIMEOUT_MS);
    try {
      return await api.getWithSignal<BffRedisBreakdownResponse>(
        `${DB_MANAGER_BASE}/bff-redis-breakdown`,
        timeoutController.signal,
      );
    } finally {
      clearTimeout(timer);
      if (signal) signal.removeEventListener("abort", onAbort);
    }
  },

  getKeyspacePageTypeCounts(keyspace: string): Promise<{
    keyspace: string;
    totalKeys: number;
    sampledKeys: number;
    counts: Record<string, number>;
    requestId: string;
  }> {
    return api.get(`${DB_MANAGER_BASE}/keyspaces/${keyspace}/page-type-counts`);
  },

  getKeyspacePageTypeStats(keyspace: string): Promise<{
    keyspace: string;
    pageTypes: Array<{
      pageType: string;
      successful: number;
      failed: number;
      totalBytes: number;
      avgDurationMs: number;
      lastSyncedAt: number;
    }>;
    requestId: string;
  }> {
    return api.get(`${DB_MANAGER_BASE}/keyspaces/${keyspace}/page-type-stats`);
  },

  async cancelSync(keyspace: string): Promise<void> {
    await api.post<{ keyspace: string; runId: string; cancelled: boolean; requestId: string }>(
      `${DB_MANAGER_BASE}/keyspaces/${keyspace}/cancel-sync`,
    );
  },

  setActiveKeyspace(
    keyspace: string,
    family = "core",
    options: { force?: boolean } = {},
  ): Promise<SetActiveKeyspaceResponse> {
    const query = options.force ? "?force=true" : "";
    return api.post<SetActiveKeyspaceResponse>(`${DB_MANAGER_BASE}/active-keyspace${query}`, {
      keyspace,
      family,
    });
  },

  generatePreviewLink(
    label: string,
    ttlSeconds: number,
    family = "core",
  ): Promise<CreatePreviewLinkResponse> {
    return api.post<CreatePreviewLinkResponse>(`${DB_MANAGER_BASE}/preview-links`, {
      label,
      ttlSeconds,
      family,
    });
  },

  getKeyspaceProgress(keyspace: string): Promise<KeyspaceProgressResponse> {
    return api.get<KeyspaceProgressResponse>(`${DB_MANAGER_BASE}/keyspaces/${keyspace}/progress`);
  },

  listAuditEntries(opts?: { limit?: number; before?: string }): Promise<AuditListResponse> {
    const params = new URLSearchParams();
    if (opts?.limit !== undefined) params.append("limit", String(opts.limit));
    if (opts?.before) params.append("before", opts.before);
    const query = params.toString();
    const endpoint = `${DB_MANAGER_BASE}/audit${query ? `?${query}` : ""}`;
    return api.get<AuditListResponse>(endpoint);
  },

  subscribeToSyncLog(
    keyspace: string,
    runId: string,
    handlers: SyncLogHandlers,
  ): SyncLogSubscription {
    const url = api.url(`${DB_MANAGER_BASE}/keyspaces/${keyspace}/syncs/${runId}/log`);
    const token = authTokenManager.getToken();
    const fullUrl = token ? `${url}?token=${encodeURIComponent(token)}` : url;
    const eventSource = new EventSource(fullUrl, { withCredentials: true });

    eventSource.onmessage = event => {
      handlers.onLine(event.data);
    };

    eventSource.addEventListener("end", event => {
      const message = event instanceof MessageEvent ? event.data : "completed";
      handlers.onEnd(typeof message === "string" ? message : "completed");
      eventSource.close();
    });

    eventSource.onerror = () => {
      if (handlers.onError) {
        handlers.onError(new Error(`SSE connection error for run ${runId}`));
      }
    };

    return {
      close: () => eventSource.close(),
    };
  },
};
